import React, { useState, useRef, useCallback } from "react";
import "./css/PhoneNumberScamCheck.css";
import "./css/SmsAnalysisSimpleMobile.css";
import "../../css/Common.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPhone,
  faExclamationTriangle,
  faShieldAlt,
  faTimes,
  faChevronDown,
  faChevronUp,
  faInfoCircle,
  faSearch,
  faDatabase,
  faArrowLeft,
  faChartBar,
  faShareAlt,
} from "@fortawesome/free-solid-svg-icons";
import InternationalPhoneInput from "../../utils/InternationalPhoneInputUtil";
import HelpGuideButton from "../../HelpGuideButton";
import EmptyStatePanel from "../common/EmptyStatePanel";
import {
  getRiskLevelIcon,
  getThreatBannerClass,
  getThreatTitle,
  getThreatSubtitle,
  isHighRisk,
  isMediumRisk,
} from "./textAnalysisUtils";
import InlineShareForm from "../common/InlineShareForm";
import useShareAlert from "../common/useShareAlert";
import { useSubscription } from "../../context/SubscriptionContext";
import DisclosuresModal from "../common/DisclosuresModal";
import { useTranslation } from "../../../../i18n/LanguageContext";

const PhoneNumberScamCheckMobile = ({
  phoneNumber,
  context,
  setContext,
  searchResults,
  isLoading,
  error,
  hasUserInput,
  phoneValidation,
  reportId,
  handlePhoneChange,
  handleSearch,
  handleClearSearch,
  handleKeyPress,
  phoneInputKey,
}) => {
  const [isDetailsExpanded, setIsDetailsExpanded] = useState(false);
  const [explanationCollapsed, setExplanationCollapsed] = useState(false);
  const [activeSlide, setActiveSlide] = useState("input");

  const { t } = useTranslation();

  const share = useShareAlert();
  const { canShare } = useSubscription();
  const isIndividualPlan = !canShare();
  const containerRef = useRef(null);

  const scrollTop = useCallback(() => {
    if (containerRef.current) {
      containerRef.current.scrollTo({ top: 0, behavior: "smooth" });
    }
  }, []);

  const showInputSlide = useCallback(() => {
    setActiveSlide("input");
    scrollTop();
  }, [scrollTop]);

  const showResultsSlide = useCallback(() => {
    setActiveSlide("results");
    scrollTop();
  }, [scrollTop]);

  // Auto-switch to results when analysis completes
  const prevResultsRef = useRef(searchResults);
  if (searchResults && searchResults !== prevResultsRef.current) {
    prevResultsRef.current = searchResults;
    if (activeSlide === "input") {
      setTimeout(() => setActiveSlide("results"), 100);
    }
  }

  const scamDetected =
    searchResults &&
    (isHighRisk(searchResults.riskLevel) ||
      isMediumRisk(searchResults.riskLevel));

  const renderInputForm = () => (
    <>
      {error && (
        <div className="error-message">
          <FontAwesomeIcon icon={faExclamationTriangle} />
          <span>{error}</span>
        </div>
      )}

      <form
        onSubmit={(e) => {
          e.preventDefault();
          handleSearch();
        }}
        className="analysis-form"
      >
        <div className="common-form-row">
          <div className="common-form-field">
            <label htmlFor="pnsc-mobile-phone-number">
              {t("phoneCheck.form.phoneLabel")}
            </label>
            <div className="phone-input-container">
              <InternationalPhoneInput
                key={phoneInputKey}
                id="pnsc-mobile-phone-number"
                value={phoneNumber}
                onChange={handlePhoneChange}
                onKeyPress={handleKeyPress}
                placeholder={t("phoneCheck.form.phonePlaceholder")}
                className="phone-input compact"
              />
              {!phoneValidation.isValid &&
                phoneValidation.isComplete &&
                phoneNumber &&
                phoneNumber.length > 2 && (
                  <div className="validation-error">
                    <FontAwesomeIcon icon={faExclamationTriangle} />
                    {t("phoneCheck.form.invalidPhone")}
                  </div>
                )}
            </div>
          </div>
        </div>

        <div className="form-group" style={{ marginTop: "1rem" }}>
          <label htmlFor="pnsc-mobile-context-input">
            <FontAwesomeIcon icon={faInfoCircle} className="label-icon" />
            {t("phoneCheck.form.contextLabel")}
          </label>
          <textarea
            id="pnsc-mobile-context-input"
            value={context}
            onChange={(e) => setContext(e.target.value)}
            placeholder={t("phoneCheck.form.contextPlaceholder")}
            className="additional-context-textarea"
            rows={4}
          />
        </div>

        <div className="btn-group">
          <button
            type="submit"
            className="btn btn-primary"
            disabled={
              isLoading ||
              !hasUserInput ||
              !phoneValidation.isValid ||
              !phoneValidation.isComplete
            }
          >
            <FontAwesomeIcon icon={faPhone} />
            {isLoading
              ? t("phoneCheck.form.analyzing")
              : t("phoneCheck.form.checkBtn")}
          </button>

          {hasUserInput && (
            <button
              type="button"
              className="btn btn-ghost"
              onClick={handleClearSearch}
              disabled={isLoading}
            >
              <FontAwesomeIcon icon={faTimes} />
              {t("phoneCheck.form.clearBtn")}
            </button>
          )}
        </div>
      </form>

      {/* "View Analysis Results" link when results exist */}
      {searchResults && (
        <button
          type="button"
          className="btn btn-secondary pnsc-view-results-link"
          onClick={showResultsSlide}
        >
          <FontAwesomeIcon icon={faChartBar} />
          {t("phoneCheck.mobile.viewResults")}
        </button>
      )}

      {/* Info Section */}
      <div className="info-section">
        <div className="tips-section">
          <h4>{t("phoneCheck.tips.heading")}</h4>
          <ul>
            <li>{t("phoneCheck.tips.tip1")}</li>
            <li>{t("phoneCheck.tips.tip2")}</li>
            <li>{t("phoneCheck.tips.tip3")}</li>
            <li className="verify-tip-container">
              {t("phoneCheck.tips.tip4")} &nbsp;&nbsp;
              <HelpGuideButton guideId={13} />
            </li>
            <li>{t("phoneCheck.tips.tip5")}</li>
            <li>{t("phoneCheck.tips.tip6")}</li>
            <li>{t("phoneCheck.tips.tip7")}</li>
          </ul>
        </div>
        <h4>{t("phoneCheck.tips.howTitle")}</h4>
        <p>{t("phoneCheck.tips.howDesc")}</p>
      </div>
    </>
  );

  const renderResultsContent = () => {
    if (!searchResults) {
      return (
        <EmptyStatePanel
          icon={faPhone}
          title={t("phoneCheck.results.readyTitle")}
          description={t("phoneCheck.results.readyDesc")}
          className="no-results"
        />
      );
    }

    return (
      <>
        {/* Threat Assessment Banner */}
        <div
          className={`threat-banner pnsc-threat-banner ${getThreatBannerClass(searchResults.riskLevel)}`}
        >
          <div className="threat-icon">
            <FontAwesomeIcon icon={getRiskLevelIcon(searchResults.riskLevel)} />
          </div>
          <h2 className="threat-title">
            {getThreatTitle(searchResults.riskLevel)}
          </h2>
          {scamDetected && (
            <button
              className="threat-banner-share-btn"
              onClick={
                isIndividualPlan
                  ? undefined
                  : (e) => {
                      e.stopPropagation();
                      share.toggleShare();
                    }
              }
              disabled={isIndividualPlan}
              title={
                isIndividualPlan
                  ? t("phoneCheck.results.shareIndividual")
                  : t("phoneCheck.results.shareFamily")
              }
            >
              <FontAwesomeIcon
                icon={faShareAlt}
                className="threat-share-icon"
              />
              <span className="threat-share-label">
                {t("phoneCheck.results.share")}
              </span>
            </button>
          )}
        </div>

        {/* Inline Share Form */}
        {share.shareOpen && scamDetected && (
          <InlineShareForm
            shareSuccess={share.shareSuccess}
            shareError={share.shareError}
            shareMessage={share.shareMessage}
            setShareMessage={share.setShareMessage}
            shareLoading={share.shareLoading}
            onCancel={share.closeShare}
            onSend={() => share.sendShare(reportId)}
          />
        )}

        {/* What Should You Do? */}
        {searchResults.recommendations &&
          searchResults.recommendations.length > 0 && (
            <div className="suggestions-section">
              <h4 className="subsection-title">
                <FontAwesomeIcon
                  icon={faInfoCircle}
                  className="fa-icon-margin-right-8"
                />
                {t("phoneCheck.results.whatToDo")}
              </h4>
              <ul className="recommendations-list">
                {searchResults.recommendations.map((rec, index) => (
                  <li key={index}>{rec}</li>
                ))}
              </ul>
            </div>
          )}

        {/* Explanation */}
        {searchResults.summary && (
          <div className="explanation-section">
            <h4
              className="subsection-title collapsible-header"
              onClick={() => setExplanationCollapsed(!explanationCollapsed)}
              style={{ cursor: "pointer" }}
            >
              <FontAwesomeIcon
                icon={faSearch}
                className="fa-icon-margin-right-8"
              />
              {t("phoneCheck.results.explanation")}
              <FontAwesomeIcon
                icon={explanationCollapsed ? faChevronDown : faChevronUp}
                style={{ marginLeft: "auto", fontSize: "0.85rem" }}
              />
            </h4>
            {!explanationCollapsed && (
              <div
                className="explanation-text"
                dangerouslySetInnerHTML={{
                  __html: searchResults.summary,
                }}
              />
            )}
          </div>
        )}

        {/* Warning */}
        <div className="warning-box pnsc-warning-box">
          <FontAwesomeIcon icon={faExclamationTriangle} />
          <div className="pnsc-warning-text">
            <strong>{t("phoneCheck.results.warningImportant")}</strong>
            <span>{t("phoneCheck.results.warningText")}</span>
          </div>
        </div>

        {/* Database Results - Collapsed by default */}
        <div className="explanation-section">
          <h4
            className="subsection-title collapsible-header"
            onClick={() => setIsDetailsExpanded(!isDetailsExpanded)}
            style={{ cursor: "pointer" }}
          >
            <FontAwesomeIcon
              icon={faDatabase}
              className="fa-icon-margin-right-8"
            />
            {t("phoneCheck.results.dbResults")}
            {searchResults.sources &&
              searchResults.sources.length > 0 &&
              ` (${searchResults.sources.length})`}
            <FontAwesomeIcon
              icon={isDetailsExpanded ? faChevronUp : faChevronDown}
              style={{ marginLeft: "auto", fontSize: "0.85rem" }}
            />
          </h4>
          {isDetailsExpanded && (
            <div style={{ marginTop: "var(--spacing-md)" }}>
              {searchResults.sources && searchResults.sources.length > 0 ? (
                searchResults.sources.map((source, index) => (
                  <div key={index} className="source-item">
                    <div className="source-header">
                      <FontAwesomeIcon
                        icon={faShieldAlt}
                        className="source-icon"
                      />
                      <h5>{source.name}</h5>
                    </div>
                    <div className="compact-analysis">
                      {source.category && (
                        <div className="analysis-line">
                          <strong>{t("phoneCheck.results.dbCategory")}</strong>{" "}
                          {source.category}
                        </div>
                      )}
                      {source.description && (
                        <div className="analysis-line">
                          <strong>{t("phoneCheck.results.dbSummary")}</strong>{" "}
                          {source.description}
                        </div>
                      )}
                      {source.details &&
                        Object.entries(source.details).map(([key, value]) => (
                          <div key={key} className="analysis-line">
                            <strong>{key}:</strong> {value}
                          </div>
                        ))}
                      {source.reports !== null &&
                        source.reports !== undefined &&
                        source.reports > 0 && (
                          <div className="analysis-line">
                            <strong>{t("phoneCheck.results.dbReports")}</strong>{" "}
                            {source.reports}
                          </div>
                        )}
                      {source.lastReported && (
                        <div className="analysis-line">
                          <strong>
                            {t("phoneCheck.results.dbLastReported")}
                          </strong>{" "}
                          {source.lastReported}
                        </div>
                      )}
                    </div>
                  </div>
                ))
              ) : (
                <p style={{ color: "var(--text-secondary)" }}>
                  {t("phoneCheck.results.dbNoSources")}
                </p>
              )}
            </div>
          )}
        </div>
      </>
    );
  };

  return (
    <div className="common-container phone-number-scam-check-container pnsc-mobile-active">
      {/* Header */}
      <div className="sasm-header">
        <div className="sasm-header-top">
          <h1 className="main-title">{t("phoneCheck.page.title")}</h1>
          <HelpGuideButton guideId={5} />
        </div>
        <p className="common-subtitle">{t("phoneCheck.page.subtitle")}</p>
      </div>

      <div className="pnsc-container" ref={containerRef}>
        <div
          className={`pnsc-view-slider ${activeSlide === "results" ? "pnsc-slide-results" : ""}`}
        >
          {/* SLIDE 1: Input Form */}
          <div className="pnsc-slide pnsc-slide-input">{renderInputForm()}</div>

          {/* SLIDE 2: Analysis Results */}
          <div className="pnsc-slide pnsc-slide-detail">
            <button
              type="button"
              className="pnsc-back-btn"
              onClick={showInputSlide}
            >
              <FontAwesomeIcon icon={faArrowLeft} />
              {t("phoneCheck.mobile.backToInputs")}
            </button>
            {renderResultsContent()}
          </div>
        </div>
      </div>

      <DisclosuresModal disclaimerText={t("phoneCheck.disclaimer")} />
    </div>
  );
};

export default PhoneNumberScamCheckMobile;
